import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { dashboardAPI, communicationAPI, schoolFeeAPI, attendanceAPI, learningMaterialsAPI, studentAPI   } from '../services/api';
import Sidebar from '../Components/Sidebar.jsx';
import Card from '../Components/Card.jsx';
import StudentIcon from '../img/student.gif';
import StaffsIcon from '../img/staffs.gif';
import CourseIcon from '../img/course.gif';
import HomeIcon from '../img/home.gif';
import AnnouncementIcon from '../img/announcement.gif';
import { PencilSquare, Trash } from 'react-bootstrap-icons';
import { Modal, Button } from 'react-bootstrap';


function CircularProgress({ percentage, color, trackColor, size = 110, stroke = 14, label, value }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const [animatedPct, setAnimatedPct] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => setAnimatedPct(percentage), 100);
    return () => clearTimeout(timeout);
  }, [percentage]);

  const offset = circumference - (animatedPct / 100) * circumference;

  return (
    <div className="hme-circle-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Track */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke={trackColor || 'rgba(255,255,255,0.06)'}
          strokeWidth={stroke}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 1.4s cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
      </svg>
      {/* Centre text */}
      <div className="hme-circle-inner">
        <span className="hme-circle-pct">{animatedPct.toFixed(1)}%</span>
        <span className="hme-circle-label">{label}</span>
        <span className="hme-circle-value">{value}</span>
      </div>
    </div>
  );
}

function Home() {
  const { user } = useSelector(state => state.auth);
  const userRole = user?.role || '';
  const [studentsCount, setStudentsCount] = useState(0);
  const [staffsCount, setStaffsCount] = useState(0);
  const [subjectsCount, setSubjectsCount] = useState(0);
  const [currentTime, setCurrentTime] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [feeTotals, setFeeTotals] = useState(null);
  const [selectedFeeYear, setSelectedFeeYear] = useState(new Date().getFullYear());
  const [feeStatusSummary, setFeeStatusSummary] = useState(null);
  const [miscFeeTypeSummary, setMiscFeeTypeSummary] = useState([]);
  const [refundSummary, setRefundSummary] = useState(null);
const [refundLoading, setRefundLoading] = useState(false);
  const [feeLoading, setFeeLoading] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [materialsSummary, setMaterialsSummary] = useState(null);
const [materialsLoading, setMaterialsLoading] = useState(false);
const [recentEnrollments, setRecentEnrollments] = useState([]);
const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

useEffect(() => {
  fetchCounts();
  fetchAnnouncements();
  if (userRole === 'admin' || userRole === 'staff') 
    fetchAttendanceSummary();
     fetchMaterialsSummary(); 
      fetchRecentEnrollments();
     fetchRefundSummary();
}, []);

// Year-filtered fee data — one sequenced effect, runs on mount and whenever the year changes
useEffect(() => {
  if (userRole !== 'admin') return;
  const loadFeeData = async () => {
    await fetchFeeTotals(selectedFeeYear);
    await fetchFeeStatusSummary(selectedFeeYear);
    await fetchMiscFeeTypeSummary(selectedFeeYear);
  };
  loadFeeData();
}, [selectedFeeYear, userRole]);

  useEffect(() => {
  const updateTime = () => {
    const now = new Date();
    setCurrentTime(now.toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    }));
  };
  updateTime();
  const timer = setInterval(updateTime, 60000);
  return () => clearInterval(timer);
}, []);

  const fetchCounts = async () => {
    try {
      const [studentsResponse, staffsResponse, subjectsResponse] = await Promise.all([
        dashboardAPI.getStudentsCount(),
        dashboardAPI.getStaffsCount(),
        dashboardAPI.getSubjectsCount(),
      ]);
      setStudentsCount(studentsResponse.data.total);
      setStaffsCount(staffsResponse.data.length);
      setSubjectsCount(subjectsResponse.data.length);
    } catch (error) {
      console.error('Error fetching counts:', error);
      setStudentsCount(0);
      setStaffsCount(0);
      setSubjectsCount(0);
    }
  };

const fetchAnnouncements = async () => {
  setIsLoading(true);
  try {
    const response = await communicationAPI.getAnnouncements();
    setAnnouncements(response.data);
  } catch (error) {
    console.error('Error fetching announcements:', error);
  } finally {
    setIsLoading(false);
  }
};

const fetchFeeTotals = async (year = selectedFeeYear) => {
  setFeeLoading(true);
  try {
    const response = await schoolFeeAPI.getTotals({ year });
    setFeeTotals(response.data.data);
  } catch (error) {
    console.error('Error fetching fee totals:', error);
  } finally {
    setFeeLoading(false);
  }
};

const fetchAttendanceSummary = async () => {
  setAttendanceLoading(true);
  try {
    const response = await attendanceAPI.getTodaySummary();
    setAttendanceSummary(response.data.data);
  } catch (error) {
    console.error('Error fetching attendance summary:', error);
  } finally {
    setAttendanceLoading(false);
  }
};

const fetchMaterialsSummary = async () => {
  setMaterialsLoading(true);
  try {
    const token = localStorage.getItem('token') ||
                  localStorage.getItem('adminToken') ||
                  localStorage.getItem('staffToken');
    const response = await learningMaterialsAPI.getSummary(token);
    setMaterialsSummary(response.data.data);
  } catch (error) {
    console.error('Error fetching materials summary:', error);
  } finally {
    setMaterialsLoading(false);
  }
};

const fetchRecentEnrollments = async () => {
  setEnrollmentsLoading(true);
  try {
    const response = await studentAPI.getRecentEnrollments();  
    setRecentEnrollments(response.data.data);
  } catch (error) {
    console.error('Error fetching recent enrollments:', error);
  } finally {
    setEnrollmentsLoading(false);
  }
};

const fetchFeeStatusSummary = async (year = selectedFeeYear) => {
  try {
    const response = await schoolFeeAPI.getPaymentStatusSummary({ year });
    setFeeStatusSummary(response.data.data);
  } catch (error) {
    console.error('Error fetching fee status summary:', error);
  }
};

const fetchMiscFeeTypeSummary = async (year = selectedFeeYear) => {
  try {
    const response = await schoolFeeAPI.getMiscFeeTypeSummary({ year });
    setMiscFeeTypeSummary(response.data.data);
  } catch (error) {
    console.error('Error fetching misc fee type summary:', error);
  }
};
  const handleEditAnnouncement = async () => {
    if (editIndex !== null && editedText.trim() !== '') {
      const updatedAnnouncement = { ...announcements[editIndex], text: editedText.trim() };
      try {
        await communicationAPI.updateAnnouncement(updatedAnnouncement.id, updatedAnnouncement);
        fetchAnnouncements();
      } catch (error) {
        console.error('Error updating announcement:', error);
      }
      setEditIndex(null);
      setEditedText('');
    }
  };

  const fetchRefundSummary = async () => {
  setRefundLoading(true);
  try {
    const response = await schoolFeeAPI.getRefundSummary();
    setRefundSummary(response.data.data);
  } catch (error) {
    console.error('Error fetching refund summary:', error);
  } finally {
    setRefundLoading(false);
  }
};

  const handleDeleteAnnouncement = async () => {
    try {
      await communicationAPI.deleteAnnouncement(announcements[deleteIndex].id);
      fetchAnnouncements();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const formatKina = (amount) => {
  const num = parseFloat(amount) || 0;
  return `K ${num.toLocaleString('en-PG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

  return (
    <>
      <div className='container-fluid'>
        <div className='row'>
          <div className='dashboard'>
            <div className="dashboard-Container">

              {/* Page Title */}
              <div className='col-12'>
                <h3 className="hme-page-title">
                  Home
                  <img className='animated-icons' src={HomeIcon} alt='home icon' />
                </h3>
              </div>

              {/* Welcome Banner */}
          <div className="hme-welcome-banner">
            <div className="hme-welcome-left">
              <span className="hme-welcome-greeting">{getGreeting()}</span>
              <span className="hme-welcome-name">{user?.name || user?.username || 'User'}</span>
              <span className={`hme-welcome-role-badge hme-welcome-role-badge--${userRole.toLowerCase()}`}>
                {userRole}
              </span>
            </div>
            <span className="hme-welcome-time">{currentTime}</span>
          </div>

              {/* Stat Cards */}
             <Card image={StudentIcon} name="Students" count={studentsCount} modifier="students" />
            <Card image={StaffsIcon} name="Staffs" count={staffsCount} modifier="staffs" />
            <Card image={CourseIcon} name="Subjects" count={subjectsCount} modifier="subjects" />

            {/* Fee Collection Widget — Admin only */}
             {userRole === 'admin' && (
  <div className="hme-fee-widget">
    <div className="hme-fee-widget-header">
      <h4 className="hme-fee-widget-heading">
        Fee Collection Summary
      </h4>
      <select
        className="hme-fee-year-select"
        value={selectedFeeYear}
        onChange={(e) => setSelectedFeeYear(parseInt(e.target.value))}
      >
        {[selectedFeeYear - 2, selectedFeeYear - 1, selectedFeeYear, selectedFeeYear + 1].map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
                  {feeLoading ? (
                    <div className='hme-loader-wrap'>
                      <div className='hme-loader-ring' />
                      <span className='hme-loader-text'>Loading Fee Data</span>
                    </div>
                  ) : feeTotals ? (
                   <div className="hme-fee-sections">

                    {/* School Fees */}
                    <div className="hme-fee-section">
                      <span className="hme-fee-section-label">School Fees</span>
                      <div className="hme-fee-section-body">

                        {/* Circular progress */}
                        {parseFloat(feeTotals.schoolFee.total_fee) > 0 && (() => {
                          const pct = Math.min(
                            (parseFloat(feeTotals.schoolFee.total_paid) / parseFloat(feeTotals.schoolFee.total_fee)) * 100,
                            100
                          );
                          return (
                            <CircularProgress
                              percentage={pct}
                              color="#66bb6a"
                              trackColor="rgba(76,175,80,0.1)"
                              label="Collected"
                              value={formatKina(feeTotals.schoolFee.total_paid)}
                            />
                          );
                        })()}

                        {/* Stat pills */}
                        <div className="hme-fee-stats">
                          <div className="hme-fee-stat hme-fee-stat--collected">
                            <span className="hme-fee-stat-value">{formatKina(feeTotals.schoolFee.total_paid)}</span>
                            <span className="hme-fee-stat-label">Collected</span>
                          </div>
                          <div className="hme-fee-stat hme-fee-stat--outstanding">
                            <span className="hme-fee-stat-value">{formatKina(feeTotals.schoolFee.total_outstanding)}</span>
                            <span className="hme-fee-stat-label">Outstanding</span>
                          </div>
                          <div className="hme-fee-stat hme-fee-stat--total">
                            <span className="hme-fee-stat-value">{formatKina(feeTotals.schoolFee.total_fee)}</span>
                            <span className="hme-fee-stat-label">Expected</span>
                          </div>
                        </div>
                        {feeStatusSummary && (
                        <div className="hme-fee-student-status">
                          <div className="hme-fee-sstatus hme-fee-sstatus--partial">
                            <span className="hme-fee-sstatus-value">{feeStatusSummary.schoolFee.partiallyPaid}</span>
                            <span className="hme-fee-sstatus-label">Partially Paid</span>
                          </div>
                          <div className="hme-fee-sstatus hme-fee-sstatus--full">
                            <span className="hme-fee-sstatus-value">{feeStatusSummary.schoolFee.fullyPaid}</span>
                            <span className="hme-fee-sstatus-label">Fully Paid</span>
                          </div>
                          <div className="hme-fee-sstatus hme-fee-sstatus--unpaid">
                            <span className="hme-fee-sstatus-value">{feeStatusSummary.schoolFee.unpaid}</span>
                            <span className="hme-fee-sstatus-label">Not Paid</span>
                          </div>
                        </div>
                      )}

                      </div>
                    </div>

                    {/* Misc Fees */}
                    <div className="hme-fee-section">
                      <span className="hme-fee-section-label">Miscellaneous Fees</span>
                      <div className="hme-fee-section-body">

                        {/* Circular progress */}
                        {parseFloat(feeTotals.miscFee.total_fee) > 0 && (() => {
                          const pct = Math.min(
                            (parseFloat(feeTotals.miscFee.total_paid) / parseFloat(feeTotals.miscFee.total_fee)) * 100,
                            100
                          );
                          return (
                            <CircularProgress
                              percentage={pct}
                              color="#ff8c00"
                              trackColor="rgba(255,140,0,0.1)"
                              label="Collected"
                              value={formatKina(feeTotals.miscFee.total_paid)}
                            />
                          );
                        })()}

                        {/* Stat pills */}
                        <div className="hme-fee-stats">
                          <div className="hme-fee-stat hme-fee-stat--collected">
                            <span className="hme-fee-stat-value">{formatKina(feeTotals.miscFee.total_paid)}</span>
                            <span className="hme-fee-stat-label">Collected</span>
                          </div>
                          <div className="hme-fee-stat hme-fee-stat--outstanding">
                            <span className="hme-fee-stat-value">{formatKina(feeTotals.miscFee.total_outstanding)}</span>
                            <span className="hme-fee-stat-label">Outstanding</span>
                          </div>
                          <div className="hme-fee-stat hme-fee-stat--total">
                            <span className="hme-fee-stat-value">{formatKina(feeTotals.miscFee.total_fee)}</span>
                            <span className="hme-fee-stat-label">Expected</span>
                          </div>
                        </div>
                      {feeStatusSummary && (
                        <div className="hme-fee-student-status">
                          <div className="hme-fee-sstatus hme-fee-sstatus--partial">
                            <span className="hme-fee-sstatus-value">{feeStatusSummary.miscFee.partiallyPaid}</span>
                            <span className="hme-fee-sstatus-label">Partially Paid</span>
                          </div>
                          <div className="hme-fee-sstatus hme-fee-sstatus--full">
                            <span className="hme-fee-sstatus-value">{feeStatusSummary.miscFee.fullyPaid}</span>
                            <span className="hme-fee-sstatus-label">Fully Paid</span>
                          </div>
                          <div className="hme-fee-sstatus hme-fee-sstatus--unpaid">
                            <span className="hme-fee-sstatus-value">{feeStatusSummary.miscFee.unpaid}</span>
                            <span className="hme-fee-sstatus-label">Not Paid</span>
                          </div>
                        </div>
                      )}
                      {miscFeeTypeSummary.length > 0 && (
                          <div className="hme-misc-type-breakdown">
                            <span className="hme-misc-type-heading">Breakdown by Fee Type</span>
                            <div className="hme-misc-type-list">
                              {miscFeeTypeSummary.map((fee, index) => (
                                <div className="hme-misc-type-row" key={fee.feeName}
                                  style={{ animationDelay: `${index * 0.07}s` }}>

                                  {/* Fee name */}
                                  <span className="hme-misc-type-name">{fee.feeName}</span>

                                  {/* Segmented bar */}
                                  <div className="hme-misc-type-bar-track">
                                    {fee.totalStudents > 0 && (
                                      <>
                                        <div
                                          className="hme-misc-type-bar-seg hme-misc-type-bar-seg--full"
                                          style={{ width: `${(fee.fullyPaid / fee.totalStudents) * 100}%` }}
                                          title={`Fully Paid: ${fee.fullyPaid}`}
                                        />
                                        <div
                                          className="hme-misc-type-bar-seg hme-misc-type-bar-seg--partial"
                                          style={{ width: `${(fee.partiallyPaid / fee.totalStudents) * 100}%` }}
                                          title={`Partially Paid: ${fee.partiallyPaid}`}
                                        />
                                        <div
                                          className="hme-misc-type-bar-seg hme-misc-type-bar-seg--unpaid"
                                          style={{ width: `${(fee.unpaid / fee.totalStudents) * 100}%` }}
                                          title={`Not Paid: ${fee.unpaid}`}
                                        />
                                      </>
                                    )}
                                  </div>

                                  {/* Counts */}
                                  <div className="hme-misc-type-counts">
                                    <span className="hme-misc-type-count hme-misc-type-count--full"
                                      title="Fully Paid">{fee.fullyPaid}</span>
                                    <span className="hme-misc-type-count hme-misc-type-count--partial"
                                      title="Partially Paid">{fee.partiallyPaid}</span>
                                    <span className="hme-misc-type-count hme-misc-type-count--unpaid"
                                      title="Not Paid">{fee.unpaid}</span>
                                  </div>

                                </div>
                              ))}
                            </div>

                            {/* Legend */}
                            <div className="hme-misc-type-legend">
                              <span className="hme-misc-type-legend-item hme-misc-type-legend-item--full">● Fully Paid</span>
                              <span className="hme-misc-type-legend-item hme-misc-type-legend-item--partial">● Partially Paid</span>
                              <span className="hme-misc-type-legend-item hme-misc-type-legend-item--unpaid">● Not Paid</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                  ) : (
                    <p className="hme-no-announcements">Fee data unavailable</p>
                  )}
                </div>
              )}

              {/* Refund Summary Widget — Admin only */}
              {userRole === 'admin' && (
                <div className="hme-refund-widget">
                  <h4 className="hme-refund-widget-heading">
                    Refund Summary
                  </h4>

                  {refundLoading ? (
                    <div className='hme-loader-wrap'>
                      <div className='hme-loader-ring' />
                      <span className='hme-loader-text'>Loading Refund Data</span>
                    </div>
                  ) : refundSummary ? (
                    <div className="hme-refund-section">
                      <div className="hme-refund-stats">
                        <div className="hme-refund-stat hme-refund-stat--refund">
                          <span className="hme-refund-stat-value">{formatKina(refundSummary.totalRefundAmount)}</span>
                          <span className="hme-refund-stat-label">Total Refunds Due</span>
                        </div>
                        <div className="hme-refund-stat hme-refund-stat--paid">
                          <span className="hme-refund-stat-value">{formatKina(refundSummary.totalPaidAmount)}</span>
                          <span className="hme-refund-stat-label">Total Fees Paid</span>
                        </div>
                        <div className="hme-refund-stat hme-refund-stat--deductions">
                          <span className="hme-refund-stat-value">{formatKina(refundSummary.totalDeductionsAmount)}</span>
                          <span className="hme-refund-stat-label">Total Deductions</span>
                        </div>
                      </div>

                      <div className="hme-fee-student-status">
                        <div className="hme-fee-sstatus hme-fee-sstatus--full">
                          <span className="hme-fee-sstatus-value">{refundSummary.eligibleCount}</span>
                          <span className="hme-fee-sstatus-label">Eligible for Refund</span>
                        </div>
                        <div className="hme-fee-sstatus hme-fee-sstatus--unpaid">
                          <span className="hme-fee-sstatus-value">{refundSummary.notEligibleCount}</span>
                          <span className="hme-fee-sstatus-label">Not Eligible</span>
                        </div>
                        <div className="hme-fee-sstatus hme-fee-sstatus--partial">
                          <span className="hme-fee-sstatus-value">{refundSummary.totalCalculated}</span>
                          <span className="hme-fee-sstatus-label">Calculations Saved</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="hme-no-announcements">Refund data unavailable</p>
                  )}
                </div>
              )}

            

              {/* Attendance Summary Widget — Admin and staff only */}
          {(userRole === 'admin' || userRole === 'staff') && (
              <div className="hme-attendance-widget">
                <h4 className="hme-attendance-widget-heading">
                  Today's Attendance
                  <span className="hme-attendance-date">{new Date().toLocaleDateString('en-PG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </h4>

                {attendanceLoading ? (
                  <div className='hme-loader-wrap'>
                    <div className='hme-loader-ring' />
                    <span className='hme-loader-text'>Loading Attendance</span>
                  </div>
                ) : attendanceSummary ? (
                  <div className="hme-attendance-content">

                    {/* Stat Pills */}
                    <div className="hme-attendance-stats">
                      <div className="hme-att-stat hme-att-stat--present">
                        <span className="hme-att-stat-value">{attendanceSummary.present}</span>
                        <span className="hme-att-stat-label">Present</span>
                      </div>
                      <div className="hme-att-stat hme-att-stat--absent">
                        <span className="hme-att-stat-value">{attendanceSummary.absent}</span>
                        <span className="hme-att-stat-label">Absent</span>
                      </div>
                      <div className="hme-att-stat hme-att-stat--late">
                        <span className="hme-att-stat-value">{attendanceSummary.late}</span>
                        <span className="hme-att-stat-label">Late</span>
                      </div>
                      <div className="hme-att-stat hme-att-stat--unmarked">
                        <span className="hme-att-stat-value">{attendanceSummary.notMarked}</span>
                        <span className="hme-att-stat-label">Not Marked</span>
                      </div>
                    </div>

                    {/* Overall progress bar */}
                    {attendanceSummary.totalStudents > 0 && (() => {
                      const presentPct = ((attendanceSummary.present / attendanceSummary.totalStudents) * 100).toFixed(1);
                      const absentPct  = ((attendanceSummary.absent  / attendanceSummary.totalStudents) * 100).toFixed(1);
                      const latePct    = ((attendanceSummary.late    / attendanceSummary.totalStudents) * 100).toFixed(1);
                      return (
                        <div className="hme-att-bar-section">
                          <div className="hme-att-bar">
                            <div className="hme-att-bar-seg hme-att-bar-seg--present" style={{ width: `${presentPct}%` }} title={`Present: ${presentPct}%`} />
                            <div className="hme-att-bar-seg hme-att-bar-seg--late"    style={{ width: `${latePct}%`    }} title={`Late: ${latePct}%`} />
                            <div className="hme-att-bar-seg hme-att-bar-seg--absent"  style={{ width: `${absentPct}%`  }} title={`Absent: ${absentPct}%`} />
                          </div>
                          <div className="hme-att-bar-legend">
                            <span className="hme-att-legend-item hme-att-legend-item--present">● Present {presentPct}%</span>
                            <span className="hme-att-legend-item hme-att-legend-item--late">● Late {latePct}%</span>
                            <span className="hme-att-legend-item hme-att-legend-item--absent">● Absent {absentPct}%</span>
                          </div>
                          <p className="hme-att-total-note">
                            {attendanceSummary.totalMarked} of {attendanceSummary.totalStudents} students marked today
                          </p>
                        </div>
                      );
                    })()}

                  </div>
                ) : (
                  <p className="hme-no-announcements">Attendance data unavailable</p>
                )}
              </div>
            )}

            {/* Learning Materials Widget — Admin only */}
            {userRole === 'admin' && (
              <div className="hme-materials-widget">
                <h4 className="hme-materials-widget-heading">
                  Learning Materials
                </h4>

                {materialsLoading ? (
                  <div className='hme-loader-wrap'>
                    <div className='hme-loader-ring' />
                    <span className='hme-loader-text'>Loading Materials</span>
                  </div>
                ) : materialsSummary ? (
                  <div className="hme-materials-content">

                    {/* Top stat pills */}
                    <div className="hme-materials-stats">
                      <div className="hme-mat-stat hme-mat-stat--total">
                        <span className="hme-mat-stat-value">{materialsSummary.totalFiles}</span>
                        <span className="hme-mat-stat-label">Total Files</span>
                      </div>
                      <div className="hme-mat-stat hme-mat-stat--week">
                        <span className="hme-mat-stat-value">{materialsSummary.thisWeek}</span>
                        <span className="hme-mat-stat-label">This Week</span>
                      </div>
                      <div className="hme-mat-stat hme-mat-stat--top">
                        <span className="hme-mat-stat-value">
                          {materialsSummary.topClass ? `Class ${materialsSummary.topClass.class}` : '—'}
                        </span>
                        <span className="hme-mat-stat-label">Most Active</span>
                      </div>
                      <div className="hme-mat-stat hme-mat-stat--date">
                        <span className="hme-mat-stat-value">
                          {materialsSummary.lastUpload
                            ? new Date(materialsSummary.lastUpload).toLocaleDateString('en-PG', { day: '2-digit', month: 'short' })
                            : '—'}
                        </span>
                        <span className="hme-mat-stat-label">Last Upload</span>
                      </div>
                    </div>

                    {/* Per-class file bars */}
                    {materialsSummary.totalFiles > 0 && (
                      <div className="hme-mat-classes">
                        <span className="hme-mat-classes-label">Files per class</span>
                        <div className="hme-mat-class-bars">
                          {materialsSummary.perClass.map((item) => {
                            const pct = ((item.count / materialsSummary.totalFiles) * 100).toFixed(0);
                            return (
                              <div className="hme-mat-class-row" key={item.class}>
                                <span className="hme-mat-class-name">Cls {item.class}</span>
                                <div className="hme-mat-bar-track">
                                  <div
                                    className="hme-mat-bar-fill"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className="hme-mat-class-count">{item.count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  </div>
                ) : (
                  <p className="hme-no-announcements">Materials data unavailable</p>
                )}
              </div>
            )}


            {/* Recent Enrollments Feed — Admin only */}
            {userRole === 'admin' && (
              <div className="hme-enrollments-widget">
                <h4 className="hme-enrollments-heading">
                  Recent Enrollments
                </h4>

                {enrollmentsLoading ? (
                  <div className='hme-loader-wrap'>
                    <div className='hme-loader-ring' />
                    <span className='hme-loader-text'>Loading Enrollments</span>
                  </div>
                ) : recentEnrollments.length === 0 ? (
                  <p className="hme-no-announcements">No enrollment records found</p>
                ) : (
                  <div className="hme-enrollments-list">
                    {recentEnrollments.map((student, index) => (
                      <div className="hme-enrollment-item" key={student.id}
                        style={{ animationDelay: `${index * 0.08}s` }}>

                        {/* Avatar */}
                        <div className={`hme-enrollment-avatar hme-enrollment-avatar--${student.gender?.toLowerCase()}`}>
                          {student.studentName?.charAt(0).toUpperCase()}
                        </div>

                        {/* Info */}
                        <div className="hme-enrollment-info">
                          <span className="hme-enrollment-name">{student.studentName}</span>
                          <span className="hme-enrollment-meta">
                            Class {student.class}
                            <span className="hme-enrollment-dot">·</span>
                            {student.gender}
                            <span className="hme-enrollment-dot">·</span>
                            {student.village}, {student.district}
                          </span>
                        </div>

                        {/* Right side */}
                        <div className="hme-enrollment-right">
                          <span className="hme-enrollment-id">{student.studentIdNumber}</span>
                          <span className="hme-enrollment-date">
                            {new Date(student.registrationDate).toLocaleDateString('en-PG', {
                              day: '2-digit', month: 'short', year: 'numeric'
                            })}
                          </span>
                        </div>

                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

              {/* Announcements Panel */}
              <div className='col-12 hme-announcements-panel'>
                <h4 className='hme-announcements-heading'>
                  Announcements
                  <img className='animated-icons' src={AnnouncementIcon} alt='announcement icon' />
                </h4>

                {isLoading ? (
                  <div className='hme-loader-wrap'>
                    <div className='hme-loader-ring' />
                    <span className='hme-loader-text'>Loading Announcements</span>
                  </div>
                ) : announcements.length === 0 ? (
                  <p className='hme-no-announcements'>No announcements right now</p>
                ) : (
                  <div className='hme-announcements-list'>
                    {announcements
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((announcement, index) => (
                        <div className="hme-announcement-item" key={index}>
                          <div className="hme-announcement-text">{announcement.text}</div>
                          <div className="hme-announcement-meta">
                            <span className="hme-announcement-timestamp">{announcement.timestamp}</span>
                            <span className="hme-announcement-user">
                              Posted by: {announcement.user} ({announcement.role})
                            </span>
                            {userRole === 'admin' && (
                              <div className="hme-announcement-actions">
                                <button
                                  className='hme-action-btn hme-action-btn--edit'
                                  onClick={() => {
                                    setEditIndex(index);
                                    setEditedText(announcement.text);
                                  }}
                                  title="Edit announcement"
                                >
                                  <PencilSquare />
                                </button>
                                <button
                                  className='hme-action-btn hme-action-btn--delete'
                                  onClick={() => {
                                    setDeleteIndex(index);
                                    setShowDeleteModal(true);
                                  }}
                                  title="Delete announcement"
                                >
                                  <Trash />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Edit Announcement Modal */}
      <Modal show={editIndex !== null} onHide={() => setEditIndex(null)} className='hme-modal'>
        <Modal.Header closeButton className='hme-modal-header'>
          <Modal.Title className='hme-modal-title'>Edit Announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body className='hme-modal-body'>
          <textarea
            className='hme-modal-textarea'
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            placeholder="Enter your edited announcement"
          />
        </Modal.Body>
        <Modal.Footer className='hme-modal-footer'>
          <Button className='hme-modal-btn hme-modal-btn--secondary' onClick={() => setEditIndex(null)}>
            Cancel
          </Button>
          <Button className='hme-modal-btn hme-modal-btn--primary' onClick={handleEditAnnouncement}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Announcement Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} className='hme-modal hme-modal--danger'>
        <Modal.Header closeButton className='hme-modal-header'>
          <Modal.Title className='hme-modal-title'>Delete Announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body className='hme-modal-body'>
          <p className='hme-modal-confirm-text'>Are you sure you want to delete this announcement?</p>
        </Modal.Body>
        <Modal.Footer className='hme-modal-footer'>
          <Button className='hme-modal-btn hme-modal-btn--secondary' onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button className='hme-modal-btn hme-modal-btn--danger' onClick={handleDeleteAnnouncement}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Home;